// src/components/dashboard/GenerationProgress.jsx
import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { postsAPI } from '../../services/api';
import {
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    SparklesIcon,
    PhotoIcon,
    RocketLaunchIcon
} from '@heroicons/react/24/outline';

const progressSteps = [
    { id: 'started', label: 'Generation Started', icon: SparklesIcon },
    { id: 'generating', label: 'AI Processing', icon: ClockIcon },
    { id: 'images', label: 'Creating Images', icon: PhotoIcon },
    { id: 'ready', label: 'Post Ready', icon: CheckCircleIcon },
    // { id: 'publishing', label: 'Publishing', icon: RocketLaunchIcon }
];

function GenerationProgress({ isGenerating, onComplete }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [startTime, setStartTime] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(60);

    // Query to check for new posts
    const { data: posts } = useQuery('posts', postsAPI.list, {
        enabled: isGenerating,
        refetchInterval: isGenerating ? 2000 : false,
        refetchIntervalInBackground: true,
    });

    // Timer for elapsed time
    useEffect(() => {
        if (isGenerating && !startTime) {
            setStartTime(Date.now());
            setCurrentStep(0);
        }

        if (!isGenerating) {
            setStartTime(null);
            setElapsedTime(0);
            setCurrentStep(0);
        }
    }, [isGenerating, startTime]);

    useEffect(() => {
        let interval;
        if (isGenerating && startTime) {
            interval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTime) / 1000);
                setElapsedTime(elapsed);

                // Update progress step based on elapsed time
                if (elapsed < 10) {
                    setCurrentStep(0); // Generation Started
                    setEstimatedTimeRemaining(50);
                } else if (elapsed < 30) {
                    setCurrentStep(1); // AI Processing
                    setEstimatedTimeRemaining(30);
                } else if (elapsed < 50) {
                    setCurrentStep(2); // Creating Images
                    setEstimatedTimeRemaining(10);
                } else {
                    setCurrentStep(3); // Post Ready (final step)
                    setEstimatedTimeRemaining(0);
                }
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isGenerating, startTime]);

    // Check for completed posts
    // Check for completed posts
    useEffect(() => {
        if (isGenerating && posts?.data?.data) {
            const recentPosts = posts.data.data.filter(post => {
                const postTime = new Date(post.createdAt).getTime();
                return startTime && (postTime >= startTime - 5000); // 5 second buffer
            });

            if (recentPosts.length > 0) {
                const latestPost = recentPosts[0];
                // Complete when post exists and is ready for review (queued status)
                if (['queued', 'approved', 'posted', 'failed'].includes(latestPost.status)) {
                    setCurrentStep(3); // Post Ready
                    setTimeout(() => {
                        onComplete && onComplete(latestPost);
                    }, 1500); // Brief delay to show completion
                }
            }
        }
    }, [posts, isGenerating, startTime, onComplete]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getStepStatus = (stepIndex) => {
        if (stepIndex < currentStep) return 'completed';
        if (stepIndex === currentStep) return 'current';
        return 'pending';
    };

    const getProgressPercentage = () => {
        const baseProgress = (currentStep / (progressSteps.length - 1)) * 80;

        // Add smooth progress within current step
        if (currentStep < progressSteps.length - 1) {
            const stepDuration = currentStep === 0 ? 10 : currentStep === 1 ? 20 : currentStep === 2 ? 20 : 10;
            const stepProgress = Math.min((elapsedTime % stepDuration) / stepDuration, 1);
            return Math.min(baseProgress + (stepProgress * 20), 95);
        }

        return baseProgress;
    };

    if (!isGenerating) return null;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8 mr-2" />
                        Generating Your Post
                    </h3>
                    <div className="text-sm text-gray-600">
                        <span className="font-medium">{formatTime(elapsedTime)}</span>
                        <span className="mx-2">•</span>
                        <span>~{formatTime(estimatedTimeRemaining)} remaining</span>
                    </div>
                </div>
            </div>

            <div className="p-6">
                {/* Progress Bar */}
                <div className="mb-6">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Progress</span>
                        <span>{Math.round(getProgressPercentage())}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                            className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${getProgressPercentage()}%` }}
                        />
                    </div>
                </div>

                {/* Steps */}
                <div className="space-y-4">
                    {progressSteps.map((step, index) => {
                        const status = getStepStatus(index);
                        const StepIcon = step.icon;

                        return (
                            <div key={step.id} className="flex items-center">
                                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-4 transition-all duration-300 ${
                                    status === 'completed'
                                        ? 'bg-green-100 text-green-600'
                                        : status === 'current'
                                            ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-300'
                                            : 'bg-gray-100 text-gray-400'
                                }`}>
                                    {status === 'completed' ? (
                                        <CheckCircleIcon className="h-5 w-5" />
                                    ) : status === 'current' ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                                        </div>
                                    ) : (
                                        <StepIcon className="h-4 w-4" />
                                    )}
                                </div>

                                <div className="flex-1">
                                    <p className={`text-sm font-medium transition-colors duration-300 ${
                                        status === 'completed'
                                            ? 'text-green-700'
                                            : status === 'current'
                                                ? 'text-blue-700'
                                                : 'text-gray-500'
                                    }`}>
                                        {step.label}
                                        {status === 'current' && (
                                            <span className="ml-2 text-xs text-blue-600 animate-pulse">
                                                In progress...
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Tips */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <div className="flex">
                        <svg className="flex-shrink-0 h-5 w-5 text-blue-400 mr-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="text-sm text-blue-700">
                            <p className="font-medium mb-1">Did you know?</p>
                            <p>
                                {currentStep === 0 && "We're analyzing your content sources to create relevant posts."}
                                {currentStep === 1 && "Our AI is crafting engaging copy tailored to your audience."}
                                {currentStep === 2 && "Beautiful images are being generated to accompany your post."}
                                {currentStep === 3 && "Your post is ready! Review and approve it to publish across platforms."}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GenerationProgress;